import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { fetchShipmentOptions, orderTrackingAction } from '../../api/orders';
import {
  inputClassName,
  labelClassName,
  primaryButtonClassName,
  secondaryButtonClassName,
} from '../../components/ui';

function isEkartName(name) {
  return String(name || '')
    .toLowerCase()
    .replace(/[\s._-]+/g, '')
    .includes('ekart');
}

function withApiCarriers(list, names) {
  const next = [...(list || [])].filter((entry) => !isEkartName(entry.code));
  for (const name of names) {
    const code = String(name || '').trim();
    if (!code || isEkartName(code)) continue;
    if (next.some((entry) => String(entry.code || '').toLowerCase() === code.toLowerCase())) {
      continue;
    }
    const otherIdx = next.findIndex((entry) => entry.code === 'Other');
    const entry = { code, label: code };
    if (otherIdx >= 0) next.splice(otherIdx, 0, entry);
    else next.push(entry);
  }
  if (!next.some((entry) => entry.code === 'Other')) {
    next.push({ code: 'Other', label: 'Other' });
  }
  return next;
}

function todayInputValue() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Amazon confirmShipment requires: orderDate <= shipDate <= now (server clock).
 * Never send a timestamp after Date.now() — noon "today" in IST can already be
 * after Amazon's UTC "now" early morning IST.
 */
function toShipDateIso(dateValue) {
  if (!dateValue) return '';

  const today = todayInputValue();
  if (dateValue >= today) {
    return new Date(Date.now() - 5000).toISOString();
  }

  const noonLocal = new Date(`${dateValue}T12:00:00`);
  if (Number.isNaN(noonLocal.getTime())) {
    return '';
  }

  if (noonLocal.getTime() > Date.now()) {
    return new Date(Date.now() - 5000).toISOString();
  }

  return noonLocal.toISOString();
}

function clampShipDate(value, minDate, maxDate) {
  let next = value || todayInputValue();
  if (minDate && next < minDate) next = minDate;
  if (maxDate && next > maxDate) next = maxDate;
  return next;
}

export default function ConfirmShipmentModal({
  isOpen,
  onClose,
  onSuccess,
  token,
  orderId,
  marketplace = 'IN',
  trackingNumber,
  amazonOrderId,
  initialShipDate = '',
  initialCarrierCode = '',
  title = 'Self Ship — Confirm shipment',
  trackingHint = 'Prefills from CRM dummy tracking — edit if your carrier uses an alphanumeric ID.',
  submitLabel = 'Confirm shipment',
  /** After dummy tracking is already on Amazon — hide ship date; only change tracking/carrier. */
  hideShipDate = false,
}) {
  const [carriers, setCarriers] = useState([]);
  const [shippingServices, setShippingServices] = useState([{ code: 'Other', label: 'Other' }]);
  const [shipDate, setShipDate] = useState(initialShipDate || todayInputValue());
  const [minShipDate, setMinShipDate] = useState('');
  const [maxShipDate, setMaxShipDate] = useState(todayInputValue());
  const [shipDateHint, setShipDateHint] = useState('');
  const [carrierCode, setCarrierCode] = useState('Other');
  const [carrierName, setCarrierName] = useState('');
  const [shippingServiceCode, setShippingServiceCode] = useState('Other');
  const [shippingMethod, setShippingMethod] = useState('Standard');
  const [trackingId, setTrackingId] = useState(trackingNumber || '');
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    let cancelled = false;
    const today = todayInputValue();
    setShipDate(clampShipDate(initialShipDate || today, '', today));
    setMinShipDate('');
    setMaxShipDate(today);
    setShipDateHint('');
    setCarrierName('');
    setShippingServiceCode('Other');
    setShippingMethod('Standard');
    setTrackingId(trackingNumber || '');
    setError('');
    setLoadingOptions(true);

    (async () => {
      try {
        const payload = await fetchShipmentOptions(token, marketplace || 'IN', {
          amazonOrderId: amazonOrderId || undefined,
        });
        if (cancelled) return;
        const preferredFromOrder = isEkartName(initialCarrierCode)
          ? 'Other'
          : String(initialCarrierCode || '').trim();
        const nextCarriers = withApiCarriers(payload.carriers || [], [
          preferredFromOrder,
          initialCarrierCode,
        ]);
        setCarriers(nextCarriers);
        setShippingServices(
          payload.shippingServices?.length
            ? payload.shippingServices
            : [{ code: 'Other', label: 'Other' }]
        );
        const preferred =
          (preferredFromOrder &&
            nextCarriers.find((entry) => entry.code === preferredFromOrder)) ||
          nextCarriers.find((entry) => entry.code === 'Other') ||
          nextCarriers[0];
        setCarrierCode(preferred?.code || 'Other');
        setCarrierName(
          preferred?.code === 'Other' && String(initialCarrierCode || '').trim()
            ? String(initialCarrierCode).trim()
            : ''
        );

        if (hideShipDate) {
          setShipDate(today);
          setMinShipDate('');
          setMaxShipDate(today);
          setShipDateHint('');
          return;
        }

        const maxDate = payload.maxShipDate || today;
        const minDate = payload.minShipDate || payload.orderDate || '';
        setMinShipDate(minDate);
        setMaxShipDate(maxDate);

        const suggested = clampShipDate(
          payload.suggestedShipDate || initialShipDate || today,
          minDate,
          maxDate
        );
        setShipDate(suggested);

        const windowBits = [];
        if (payload.earliestShipDate) {
          windowBits.push(`earliest ${payload.earliestShipDate}`);
        }
        if (payload.latestShipDate) {
          windowBits.push(`ship by ${payload.latestShipDate}`);
        }

        if (payload.shipDateWarning) {
          setShipDateHint(payload.shipDateWarning);
        } else if (payload.shipDateSource === 'amazon' && windowBits.length) {
          setShipDateHint(
            `Amazon window (${windowBits.join(', ')}). Ship date must be on or before today — Amazon rejects future dates.`
          );
        } else {
          setShipDateHint(
            'Amazon requires ship date between order date and now (not in the future).'
          );
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Unable to load Amazon carrier list.');
          setShipDate(clampShipDate(initialShipDate || today, '', today));
          setMaxShipDate(today);
        }
      } finally {
        if (!cancelled) {
          setLoadingOptions(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, token, marketplace, amazonOrderId, initialShipDate, initialCarrierCode, trackingNumber, hideShipDate]);

  if (!isOpen) {
    return null;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!hideShipDate) {
      if (!shipDate) {
        setError('Ship date is required.');
        return;
      }

      const today = todayInputValue();
      if (shipDate > today || (maxShipDate && shipDate > maxShipDate)) {
        setError(
          `Ship date cannot be in the future. Amazon only accepts dates up to today (${maxShipDate || today}).`
        );
        return;
      }

      if (minShipDate && shipDate < minShipDate) {
        setError(`Ship date cannot be before the order date (${minShipDate}).`);
        return;
      }
    }

    if (!carrierCode) {
      setError('Carrier is required.');
      return;
    }

    if (carrierCode === 'Other' && !carrierName.trim()) {
      setError('Enter a carrier name when Carrier is Other.');
      return;
    }

    if (shippingServiceCode === 'Other' && !shippingMethod.trim()) {
      setError(
        'Enter a shipping service name, or keep Other only if Amazon accepts blank — enter a valid service name.'
      );
      return;
    }

    const tracking = trackingId.trim();
    if (!tracking) {
      setError('Tracking ID is required.');
      return;
    }

    setSubmitting(true);
    try {
      const pushBody = {
        carrierCode,
        carrierName: carrierCode === 'Other' ? carrierName.trim() : undefined,
        shippingMethod:
          shippingServiceCode === 'Other'
            ? shippingMethod.trim() || 'Other'
            : shippingServiceCode,
        trackingNumber: tracking,
        // I2I / re-confirm: never send today's date — backend keeps the original Amazon ship date.
        ...(hideShipDate
          ? { preserveShipDate: true, hideShipDate: true }
          : { shipDate: toShipDateIso(shipDate) }),
      };

      if (!hideShipDate && !pushBody.shipDate) {
        setError('Invalid ship date.');
        setSubmitting(false);
        return;
      }

      const payload = await orderTrackingAction(token, orderId, 'push', pushBody);
      onSuccess?.(payload);
      onClose?.();
    } catch (err) {
      setError(err.message || 'Failed to confirm shipment on Amazon.');
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[200] grid place-items-center bg-slate-900/40 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-shipment-title"
        className="w-full max-w-lg rounded-xl border border-slate-200 bg-white p-5 shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 id="confirm-shipment-title" className="text-base font-semibold text-slate-900">
              {title}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              {amazonOrderId
                ? `Order ${amazonOrderId}`
                : hideShipDate
                  ? 'Update tracking on Amazon Seller Central'
                  : 'Confirm shipment on Amazon Seller Central'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-2 py-1 text-sm text-slate-500 hover:bg-slate-100"
          >
            Close
          </button>
        </div>

        <form className="grid gap-4" onSubmit={handleSubmit}>
          {!hideShipDate ? (
            <label className="grid gap-1.5">
              <span className={labelClassName}>Ship date</span>
              <input
                type="date"
                value={shipDate}
                min={minShipDate || undefined}
                max={maxShipDate || todayInputValue()}
                onChange={(event) =>
                  setShipDate(
                    clampShipDate(event.target.value, minShipDate, maxShipDate || todayInputValue())
                  )
                }
                className={inputClassName}
                required
              />
              {shipDateHint ? <span className="text-xs text-slate-500">{shipDateHint}</span> : null}
            </label>
          ) : null}

          <label className="grid gap-1.5">
            <span className={labelClassName}>Carrier</span>
            <select
              value={carrierCode}
              onChange={(event) => setCarrierCode(event.target.value)}
              className={inputClassName}
              disabled={loadingOptions || !carriers.length}
              required
            >
              {carriers.map((entry) => (
                <option key={entry.code} value={entry.code}>
                  {entry.label}
                </option>
              ))}
            </select>
            <span className="text-xs text-slate-500">
              Amazon carrier catalog ({carriers.length} carriers). Default is the ITL booked courier
              when it matches.
            </span>
          </label>

          {carrierCode === 'Other' ? (
            <label className="grid gap-1.5">
              <span className={labelClassName}>Carrier name</span>
              <input
                type="text"
                value={carrierName}
                onChange={(event) => setCarrierName(event.target.value)}
                className={inputClassName}
                placeholder="Enter carrier name"
                required
              />
            </label>
          ) : null}

          <label className="grid gap-1.5">
            <span className={labelClassName}>Shipping service</span>
            <select
              value={shippingServiceCode}
              onChange={(event) => setShippingServiceCode(event.target.value)}
              className={inputClassName}
            >
              {shippingServices.map((entry) => (
                <option key={entry.code} value={entry.code}>
                  {entry.label}
                </option>
              ))}
            </select>
          </label>

          {shippingServiceCode === 'Other' ? (
            <label className="grid gap-1.5">
              <span className={labelClassName}>Shipping service name</span>
              <input
                type="text"
                value={shippingMethod}
                onChange={(event) => setShippingMethod(event.target.value)}
                className={inputClassName}
                placeholder="Enter a valid shipping service name"
                required
              />
            </label>
          ) : null}

          <label className="grid gap-1.5">
            <span className={labelClassName}>Tracking ID</span>
            <input
              type="text"
              value={trackingId}
              onChange={(event) => setTrackingId(event.target.value)}
              className={inputClassName}
              placeholder="Tracking ID (letters and numbers allowed)"
              required
              autoComplete="off"
              spellCheck={false}
            />
            <span className="text-xs text-slate-500">
              {trackingHint}
            </span>
          </label>

          {error ? (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </p>
          ) : null}

          <div className="mt-1 flex justify-end gap-2">
            <button type="button" className={secondaryButtonClassName} onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button
              type="submit"
              className={primaryButtonClassName}
              disabled={submitting || loadingOptions || !trackingId.trim()}
            >
              {submitting ? (hideShipDate ? 'Updating…' : 'Confirming…') : submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
